import { useMemo } from 'react';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { TUser } from '@rbx/auth';
import { Configuration } from '@rbx/clients-core';
import { UsersApi } from '@rbx/client-users/v1';
import { StatusCodes } from '@rbx/core';
import { getBEDEV1ServiceBasePath } from '../utils/getBasePaths';
import useNavigationConfigs from '../hooks/useNavigationConfigs';
import isResponseError from '../utils/isResponseError';

const useGetAuthenticatedUser = (options?: Partial<UseQueryOptions<TUser | null>>) => {
  const { target, robloxEnvironment: environment } = useNavigationConfigs();
  const userApi = useMemo(() => {
    const basePath = getBEDEV1ServiceBasePath('users', target, environment);

    const configuration = new Configuration({
      basePath,
      credentials: 'include',
    });

    return new UsersApi(configuration);
  }, [environment, target]);

  return useQuery({
    enabled: options?.enabled,
    queryKey: ['AuthenticatedUser'],
    queryFn: async () => {
      try {
        const response = await userApi.v1UsersAuthenticatedGet();

        return response as TUser;
      } catch (error) {
        if (isResponseError(error) && error.response.status === StatusCodes.UNAUTHORIZED) {
          return null;
        }
        throw error;
      }
    },
    refetchOnWindowFocus: options?.refetchOnWindowFocus,
  });
};

export default useGetAuthenticatedUser;
